import React from 'react';
import { motion, Variants } from 'framer-motion';
import Header from '../../organisms/Header/Header';
import arbventureHeroMobile from '../../../assets/images/arbventure/hero_mobile.webp';
import Constants from '../../../app/utils/constants';
import colors from '../../theme/color';

const HeroSection = ({ startAnimation }: { startAnimation: boolean }) => {
  const highlightCards = Constants.arbventureHeroHighlights;
  const [hoveredCard, setHoveredCard] = React.useState<string | null>(null);

  const cardCopyClassName =
    'mt-3 font-gilroy-medium   text-[0.86rem] leading-[1.24] text-left md:text-[1rem] lg:text-[0.9rem] xl:text-[1.05rem] 2xl:text-[1.4rem] lg:mt-4 lg:text-[1rem] lg:leading-[1.2]  min-[2560px]:text-[1.45rem] min-[2560px]:mt-5';

  const leftVariant: Variants = {
    hidden: { opacity: 0, x: -150 },
    visible: {
      opacity: 1,
      x: 0,
      transition: { duration: 0.8, ease: 'easeIn' },
    },
  };

  const rightVariant: Variants = {
    hidden: { opacity: 0, x: 150 },
    visible: {
      opacity: 1,
      x: 0,
      transition: { duration: 0.8, ease: 'easeIn' },
    },
  };

  const scrollLeftVariant: Variants = {
    hidden: { opacity: 0, x: -150, scale: 0.75 },
    visible: {
      opacity: 1,
      x: 0,
      scale: 1,
      transition: { duration: 0.7, ease: 'easeOut' },
    },
  };

  const scrollRightVariant: Variants = {
    hidden: { opacity: 0, x: 150, scale: 0.75 },
    visible: {
      opacity: 1,
      x: 0,
      scale: 1,
      transition: { duration: 0.7, ease: 'easeOut' },
    },
  };

  const cardVariant: Variants = {
    hidden: { opacity: 0, scale: 0.75 },
    visible: (i: number) => ({
      opacity: 1,
      scale: 1,
      transition: {
        duration: 0.7,
        ease: 'easeOut',
        delay: i * 0.15,
      },
    }),
  };

  return (
    <section className="relative w-full pb-4 md:pb-6 lg:pb-8">
      <div className="pointer-events-none absolute inset-0 overflow-hidden">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,_rgba(255,255,255,0.02)_0%,_rgba(255,255,255,0)_58%)]" />
      </div>

      <div className="relative w-full">
        <Header />
      </div>

      <div className="relative mx-auto mt-14 w-full px-5 md:mt-18 md:px-2 lg:mt-22 lg:px-12 2xl:px-16">
        
        {/* ✅ REMOVED minmax + max width constraints */}
        <div className="grid items-center gap-10 px-5 md:grid-cols-2 lg:gap-6 xl:grid-cols-2 min-[2560px]:grid-cols-2 min-[2560px]:gap-10">

          <motion.div
            variants={leftVariant}
            initial="hidden"
            animate={startAnimation ? 'visible' : 'hidden'}
            className="xl:ml-[20%]  lg:-translate-y-6 lg:translate-x-10 "
          >
            <h1
              className="font-rajdhani text-center md:text-start text-[3rem] font-bold uppercase leading-[0.9] tracking-[0.01em] sm:text-[3rem] md:text-[4rem] lg:text-[4.3rem] xl:text-[5rem] 2xl:text-[6.35rem]"
              style={{ color: colors.arbVentureAccent }}
            >
              ARB VENTURES
            </h1>
            <p className="mt-5 text-center md:text-start font-gilroy-bold text-[1.3rem] leading-[1.08] text-white sm:text-[1.3rem] md:text-[1.7rem] lg:text-[1.9rem] xl:text-[2.3rem] 2xl:text-[3.10rem]">
              Real Estate
              <br />
              Management Platform
            </p>
          </motion.div>

          <motion.div
  variants={rightVariant}
  initial="hidden"
  animate={startAnimation ? 'visible' : 'hidden'}
  className="relative mx-auto flex w-full justify-center lg:justify-end"
>

  {/* GLOW */}
  <div
    className="absolute left-1/2 top-1/2 z-0 -translate-x-1/2 -translate-y-1/2 rounded-full blur-[60px] w-full h-full md:w-[16rem] md:blur-[70px] lg:h-[350px] lg:w-[300px] lg:blur-[80px] xl:h-[400px] xl:w-[450px] xl:blur-[90px] 2xl:h-[550px] 2xl:w-[550px]"
    style={{
      backgroundImage: `radial-gradient(circle at 60% 40%, rgba(95, 153, 55, 0.9) 0%, rgba(95, 153, 55, 0.7) 40%, rgba(95, 153, 55, 0.35) 65%, rgba(95, 153, 55, 0) 85%)`,
    }}
  />

  {/* FLOATING IMAGE */}
  <motion.img
    src={arbventureHeroMobile}
    alt="ARB Ventures mobile application preview"
    animate={{
      y: [0, -18, 0, 18, 0],
    }}
    transition={{
      duration: 6,
      repeat: Infinity,
      ease: 'easeInOut',
    }}
    className="relative z-10 w-full object-contain drop-shadow-[0_28px_55px_rgba(0,0,0,0.58)] h-[250px] md:h-[400px] lg:h-[450px] xl:h-[600px] 2xl:h-[700px]"
  />
</motion.div>
        </div>

        {/* ✅ REMOVED ALL max-w from cards grid */}
        <div className="mx-auto mt-10 xl:mt-20 grid w-full grid-cols-1 gap-4 sm:grid-cols-3 md:mt-12 md:gap-5 lg:mt-10 lg:gap-8 xl:gap-8 2xl:w-[75%]">
  {highlightCards.map((card, i) => {
    const isTechCard = card.title === 'Tech Used';
    const isHovered = hoveredCard === card.title;

    const scrollVariant =
      i === 0
        ? scrollLeftVariant
        : i === highlightCards.length - 1
        ? scrollRightVariant
        : cardVariant;

    return (
      <motion.div
        key={card.title}
        custom={i}
        variants={scrollVariant}
        initial="hidden"
        whileInView="visible"
        viewport={{ once: false, amount: 0.3 }}
        className="h-full"
      >
        <motion.div
          whileHover={{ scale: 1.05 }}
          transition={{ type: 'spring', stiffness: 300, damping: 20 }}
          className="h-full rounded-[18px] p-[1px] shadow-[0_18px_34px_rgba(0,0,0,0.25)]"
          onMouseEnter={() => setHoveredCard(card.title)}
          onMouseLeave={() => setHoveredCard(null)}
          style={{
            backgroundImage: isHovered
              ? `linear-gradient(180deg, ${colors.arbVentureAccent} 0%, ${colors.arbVentureAccent} 100%)`
              : `linear-gradient(138.65deg, rgba(111,175,56,0.95) 0%, rgba(111,175,56,0.35) 24%, rgba(111,175,56,0.08) 43%, rgba(111,175,56,0) 64%)`,
          }}
        >
          <article
            className=" 
              flex h-full flex-col
              rounded-[17px]
              px-5 py-5
              sm:px-4
              md:px-5
              lg:px-6 lg:py-7
              xl:px-7 xl:py-8
              2xl:px-10 2xl:py-10

              min-h-[120px]
              md:min-h-[150px]
              lg:min-h-[170px]
              xl:min-h-[220px]
              2xl:min-h-[250px]

              transition-colors duration-300 
            "
            style={{
              backgroundColor: isHovered
                ? colors.arbVentureAccent
                : `${colors.arbVentureCardBg}F2`,
            }}
          >
            <h2
              className="
                font-gilroy-bold leading-none
                text-[20px]
                md:text-[20px]
                lg:text-[22px]
                xl:text-[30px]
                2xl:text-[40px]
              "
              style={{
                color: isHovered
                  ? '#15210F'
                  : colors.arbVentureAccentBright,
              }}
            >
              {card.title}
            </h2>

            {isTechCard ? (
              <div className={`${cardCopyClassName} flex flex-col gap-[4px]`}>
              {card.items.map((item, index) => (
                <p key={index}>{item}</p>
              ))}
            </div>
            ) : (
              <ul className={`${cardCopyClassName} space-y-[4px]`}>
                {card.items.map((item) => (
                  <li key={item}>{item}</li>
                ))}
              </ul>
            )}
          </article>
        </motion.div>
      </motion.div>
    );
  })}
</div>
      </div>
    </section>
  );
};

export default HeroSection;